import { useEffect, useState, useCallback } from "react";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { http } from "@/lib/http";

const supportsWebAuthn = () => typeof window !== "undefined" && !!window.PublicKeyCredential;

export function useWebAuthnSupport() {
  const [state, setState] = useState({ supported: false, platform: false, conditional: false });
  useEffect(() => {
    (async () => {
      if (!supportsWebAuthn()) return;
      const P = window.PublicKeyCredential;
      const platform = P.isUserVerifyingPlatformAuthenticatorAvailable
        ? await P.isUserVerifyingPlatformAuthenticatorAvailable().catch(() => false)
        : false;
      const conditional = P.isConditionalMediationAvailable
        ? await P.isConditionalMediationAvailable().catch(() => false)
        : false;
      setState({ supported: true, platform, conditional });
    })();
  }, []);
  return state;
}

export function usePasskey() {
  const registerPasskey = useCallback(async (deviceLabel = "") => {
    const { data: begin } = await http.post("/auth/webauthn/register/begin", { device_label: deviceLabel });
    const credential = await startRegistration({ optionsJSON: begin.options });
    const { data: done } = await http.post("/auth/webauthn/register/complete", {
      ceremony_id: begin.ceremony_id,
      credential,
      device_label: deviceLabel,
    });
    return done;
  }, []);

  const loginPasskey = useCallback(async (email = null, { useAutofill = false, signal = null } = {}) => {
    const { data: begin } = await http.post("/auth/webauthn/login/begin", { email });
    const credential = await startAuthentication({
      optionsJSON: begin.options,
      useBrowserAutofill: useAutofill,
      signal: signal || undefined,
    });
    const { data: done } = await http.post("/auth/webauthn/login/complete", {
      ceremony_id: begin.ceremony_id,
      credential,
    });
    return done;
  }, []);

  const listPasskeys = useCallback(async () => (await http.get("/auth/webauthn")).data, []);
  const deletePasskey = useCallback(async (credentialId) => (await http.delete(`/auth/webauthn/${credentialId}`)).data, []);

  return { registerPasskey, loginPasskey, listPasskeys, deletePasskey };
}
